import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from 'firebase/storage'
import {
  COLLECTIONS,
  extensionForContentType,
  isAcceptedUploadType,
  MAX_UPLOAD_BYTES,
  originalReceiptPath,
  receiptSchema,
  type CaptureSource,
  type Receipt,
} from '@budgetmonkey/shared'
import { httpsCallable } from 'firebase/functions'
import { db, functions, storage } from '@/lib/firebase'
import { nowIso, toIso } from '@/lib/firestore'

const TIMESTAMP_FIELDS = [
  'capturedAt',
  'extractedDate',
  'linkedAt',
  'createdAt',
  'updatedAt',
] as const

// No `withConverter` here on purpose. The converter throws on a schema
// mismatch, which is correct for reading one document but fatal for a list —
// a single malformed receipt would take the whole queue down. Reads below
// validate with the same schema, per document, so one bad record stays one
// bad record. Writes are plain objects and don't need the converter.

/**
 * All receipts for a user, newest capture first.
 *
 * Not scoped by fiscal year: a receipt captured in June for a June purchase
 * can still be sitting unlinked in July, and the review queue must not hide it
 * just because the year rolled over.
 *
 * **One unreadable document must not empty the screen.** The converter throws
 * on a schema mismatch, which is right for a single document — a malformed
 * record should announce itself rather than propagate a NaN into a total. But
 * mapping it across a list turned one bad receipt into "Couldn't load
 * receipts" for all of them, hiding every good receipt behind a record the
 * user has no way to reach and delete. That happened for real: an inbound HTML
 * email was written without its `originalFileUrl` and took the whole queue
 * down with it.
 *
 * So each document is parsed on its own. The good ones render; the bad ones
 * are counted and reported, never silently dropped — §9 does not allow a
 * capture to vanish quietly, and a record too broken to display is still a
 * record of something that arrived.
 */
export interface ReceiptsResult {
  receipts: Receipt[]
  /** Documents that failed validation, with why, for a visible warning. */
  unreadable: { id: string; reason: string }[]
}

export async function fetchReceiptsWithProblems(
  userId: string,
): Promise<ReceiptsResult> {
  const snapshot = await getDocs(
    query(
      collection(db, COLLECTIONS.receipts),
      where('userId', '==', userId),
      orderBy('capturedAt', 'desc'),
    ),
  )

  const receipts: Receipt[] = []
  const unreadable: { id: string; reason: string }[] = []

  for (const doc of snapshot.docs) {
    const raw: Record<string, unknown> = { ...doc.data(), id: doc.id }
    for (const field of TIMESTAMP_FIELDS) {
      if (field in raw) raw[field] = toIso(raw[field])
    }

    const parsed = receiptSchema.safeParse(raw)
    if (parsed.success) {
      receipts.push(parsed.data)
    } else {
      unreadable.push({
        id: doc.id,
        reason: parsed.error.issues
          .map((issue) => `${issue.path.join('.')} ${issue.message}`)
          .join('; '),
      })
    }
  }

  return { receipts, unreadable }
}

export async function fetchReceipts(userId: string): Promise<Receipt[]> {
  return (await fetchReceiptsWithProblems(userId)).receipts
}

export interface UploadHandle {
  receiptId: string
  storagePath: string
  /** Resolves when both the file and its Firestore document are written. */
  done: Promise<Receipt>
  cancel: () => void
}

export class UploadRejected extends Error {
  // Declared and assigned explicitly rather than as a constructor parameter
  // property — `erasableSyntaxOnly` rejects those, since they emit runtime code.
  readonly reason: 'type' | 'size'

  constructor(message: string, reason: 'type' | 'size') {
    super(message)
    this.name = 'UploadRejected'
    this.reason = reason
  }
}

/**
 * Upload a capture — REQUIREMENTS.md §6.3, §9.
 *
 * The file goes to Storage **before** the Firestore document exists. §9
 * requires that any capture reaching the system is recoverable even if
 * processing fails, and Storage is the durable half: a byte-complete file with
 * no document can be reconciled by listing the bucket, whereas a document
 * pointing at a failed upload is a dangling reference.
 *
 * The document ID is generated locally so it can name the storage path before
 * either write happens, which is what lets the Storage trigger in P2.4 find
 * the right receipt from the path alone.
 */
/**
 * Convert an iPhone HEIC to JPEG before upload, on the device that made it.
 *
 * This is the right place to do it, not the server. HEIC is Apple's format and
 * every Apple device decodes it natively, so the browser here already has a
 * working codec. The server has libheif, which choked on a real iPhone photo
 * twice — first on a reference-count limit, then inside its decoder plugin.
 * Converting where the codec is known-good removes a whole class of failure
 * rather than chasing it.
 *
 * The JPEG becomes the stored capture. Nothing is lost that we ever had: the
 * alternative is a HEIC nothing downstream can open, including the review
 * screen. Resolution and orientation are preserved — this is a format change,
 * not a resize; the server still downscales for the vision API.
 *
 * Any failure returns the original untouched. A browser without a HEIC decoder
 * (anything non-Apple) falls through to the server path, which is exactly
 * where it was before.
 */
/** Wrap a JPEG blob as a File, keeping the name but changing the extension. */
function asJpegFile(blob: Blob, original: File): File {
  return new File([blob], original.name.replace(/\.hei[cf]$/i, '.jpg'), {
    type: 'image/jpeg',
    lastModified: original.lastModified,
  })
}

/** The browser's own decoder. Works in Safari; desktop Chrome cannot do it. */
async function decodeHeicNatively(file: File): Promise<File | null> {
  const bitmap = await createImageBitmap(file)
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height

  const context = canvas.getContext('2d')
  if (context === null) return null
  context.drawImage(bitmap, 0, 0)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92),
  )
  return blob === null ? null : asJpegFile(blob, file)
}

/**
 * Convert an iPhone photo to something the extraction can read.
 *
 * Three attempts, in cost order. `createImageBitmap` is free where it works —
 * Safari decodes HEIC natively — but desktop Chrome cannot, and *that* is why
 * uploads from the computer arrived as `.heic` and failed server-side while
 * the same photo from the phone was fine. So a WASM decoder backs it up,
 * loaded only when a HEIC is actually chosen rather than shipped to everyone
 * who opens the app.
 *
 * If both fail this **throws**. The previous version returned the original
 * file on failure, which quietly uploaded bytes we already knew the server
 * could not decode — the error then surfaced days later as a receipt that
 * never extracted, with nothing connecting it to the moment of upload.
 */
export async function convertHeicForUpload(file: File): Promise<File> {
  if (!/^image\/hei[cf]$/i.test(file.type)) return file

  try {
    const native = await decodeHeicNatively(file)
    if (native !== null) return native
  } catch {
    // Expected on Chrome. Fall through to the decoder that does not need the
    // browser's help.
  }

  try {
    const { heicTo } = await import('heic-to')
    const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 })
    return asJpegFile(blob, file)
  } catch (cause) {
    throw new UploadRejected(
      `This iPhone photo couldn't be converted (${
        cause instanceof Error ? cause.message : String(cause)
      }). Open it on a Mac and export as JPEG, or send it from your phone.`,
      'type',
    )
  }
}

/**
 * Why this file can't be uploaded, or null if it can.
 *
 * Separated from the upload itself so the offline path can reject a bad file
 * immediately rather than queueing it and failing tomorrow, when the user is
 * nowhere near the receipt.
 */
export function rejectUploadReason(file: File): UploadRejected | null {
  if (!isAcceptedUploadType(file.type)) {
    return new UploadRejected(
      file.type === ''
        ? 'That file has no recognizable type. Try a photo or a PDF.'
        : `${file.type} isn't a receipt format. Use a photo (JPEG, PNG, HEIC) or a PDF.`,
      'type',
    )
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return new UploadRejected(
      `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`,
      'size',
    )
  }
  return null
}

export function uploadReceipt(
  userId: string,
  file: File,
  captureSource: CaptureSource,
  onProgress?: (fraction: number) => void,
): UploadHandle {
  const rejection = rejectUploadReason(file)
  if (rejection !== null) throw rejection

  const receiptId = doc(collection(db, COLLECTIONS.receipts)).id
  const storagePath = originalReceiptPath(
    userId,
    receiptId,
    extensionForContentType(file.type),
  )

  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: file.type,
    customMetadata: { receiptId, userId, captureSource },
  })

  const done = new Promise<Receipt>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        onProgress?.(
          snapshot.totalBytes === 0 ? 0 : snapshot.bytesTransferred / snapshot.totalBytes,
        )
      },
      reject,
      () => {
        void (async () => {
          try {
            const timestamp = nowIso()

            // Only the fields this client owns, and merged rather than set.
            //
            // The Storage trigger fires on the uploaded file, not on this
            // document, so extraction can finish and write its results *before*
            // this line runs — the function creates the document if it has to.
            // A plain `setDoc` would then silently erase a completed reading,
            // and writing `extractedVendor: null` here would erase it even on a
            // merge. The client owns provenance, the function owns everything
            // it read, and neither writes the other's fields — so the order the
            // two land in stops mattering.
            const captured = {
              userId,
              captureSource,
              originalFileUrl: storagePath,
              renderedPdfUrl: null,
              pageImageUrls: [],
              emailFromAddress: null,
              emailSubject: null,
              rawEmailUrl: null,
              emailSenderTrusted: true,
              capturedAt: timestamp,
              linkedTransactionId: null,
              splitGroupId: null,
              linkedAt: null,
              userOverride: false,
              createdAt: timestamp,
              updatedAt: timestamp,
            }

            await setDoc(doc(db, COLLECTIONS.receipts, receiptId), {
              ...captured,
              capturedAt: new Date(timestamp),
              linkedAt: null,
              createdAt: new Date(timestamp),
              updatedAt: new Date(timestamp),
            }, { merge: true })

            // What the caller gets back is the optimistic local view; the
            // extraction fields fill in when the function's write arrives.
            resolve({
              ...captured,
              id: receiptId,
              extractedVendor: null,
              extractedAmount: null,
              extractedDate: null,
              extractionConfidence: null,
              extractionModel: null,
              extraction: null,
            })
          } catch (cause) {
            reject(cause instanceof Error ? cause : new Error(String(cause)))
          }
        })()
      },
    )
  })

  return { receiptId, storagePath, done, cancel: () => task.cancel() }
}

/** Signed URL for displaying a stored file. */
export async function receiptFileUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath))
}

export type ReceiptUpdate = Partial<
  Pick<
    Receipt,
    | 'extractedVendor'
    | 'extractedAmount'
    | 'extractedDate'
    | 'linkedTransactionId'
    | 'splitGroupId'
    | 'linkedAt'
    | 'userOverride'
    | 'emailSenderTrusted'
  >
>

export async function updateReceipt(id: string, patch: ReceiptUpdate): Promise<void> {
  const { extractedDate, linkedAt, ...rest } = patch
  await updateDoc(doc(db, COLLECTIONS.receipts, id), {
    ...rest,
    ...(extractedDate !== undefined
      ? { extractedDate: extractedDate === null ? null : new Date(extractedDate) }
      : {}),
    ...(linkedAt !== undefined
      ? { linkedAt: linkedAt === null ? null : new Date(linkedAt) }
      : {}),
    updatedAt: new Date(),
  })
}

/**
 * Ask the server to read this receipt again — REQUIREMENTS.md §9.
 *
 * The Storage trigger deliberately doesn't auto-retry, so this is the only way
 * a receipt that failed extraction ever gets read. The function re-checks
 * ownership itself; it runs with admin credentials, where the security rules
 * do not apply.
 */
export async function reprocessReceipt(receiptId: string): Promise<void> {
  const call = httpsCallable<{ receiptId: string }, unknown>(
    functions,
    'reprocessReceipt',
  )
  await call({ receiptId })
}

/**
 * Attach a receipt to a transaction — REQUIREMENTS.md §7.1 step 7.
 *
 * The link is stored on both sides: `receipts.linkedTransactionId` drives the
 * review queue, `transactions.receiptIds` drives the packet. One batch, so a
 * receipt can never be marked filed against a transaction that doesn't list it
 * — that mismatch is invisible until packet time, which is the worst moment to
 * discover it.
 */
export async function linkReceiptToTransaction(
  receiptId: string,
  transactionId: string,
): Promise<void> {
  const batch = writeBatch(db)
  const now = new Date()
  const receiptRef = doc(db, COLLECTIONS.receipts, receiptId)

  // One bill, several charges, is the ordinary case: an airline bills a
  // segment, a seat and a fee separately; a hotel takes a deposit and then a
  // balance; an Uber tip lands a week after the fare. So attaching does not
  // move the receipt — it adds a charge to the ones it is already on.
  //
  // `linkedTransactionId` is the *primary* (schema §5.4), kept as the first
  // charge it was filed against so the review queue has a stable answer. The
  // full set lives in `transactions.receiptIds`, which is what the packet
  // prints from and what the receipts screen now reads.
  const existing = await getDoc(receiptRef)
  const primary = existing.get('linkedTransactionId') as string | null | undefined

  batch.update(receiptRef, {
    ...(primary == null ? { linkedTransactionId: transactionId, linkedAt: now } : {}),
    updatedAt: now,
  })
  batch.update(doc(db, COLLECTIONS.transactions, transactionId), {
    receiptIds: arrayUnion(receiptId),
    updatedAt: now,
  })

  await batch.commit()
}

/**
 * Detach a receipt from one charge.
 *
 * Named rather than implied: a receipt can be on several charges, and
 * detaching from the one being looked at must not strip it from the others.
 * When the charge was the primary, the primary moves to whichever charge is
 * left — and only becomes null when none is, which is what returns the receipt
 * to the review queue.
 */
export async function unlinkReceiptFromTransaction(
  receipt: Receipt,
  transactionId: string,
): Promise<void> {
  const batch = writeBatch(db)
  const now = new Date()

  batch.update(doc(db, COLLECTIONS.transactions, transactionId), {
    receiptIds: arrayRemove(receipt.id!),
    updatedAt: now,
  })

  if (receipt.linkedTransactionId === transactionId) {
    const remaining = await getDocs(
      query(
        collection(db, COLLECTIONS.transactions),
        where('userId', '==', receipt.userId),
        where('receiptIds', 'array-contains', receipt.id!),
      ),
    )
    const next = remaining.docs.map((d) => d.id).find((id) => id !== transactionId)
    batch.update(doc(db, COLLECTIONS.receipts, receipt.id!), {
      linkedTransactionId: next ?? null,
      linkedAt: next === undefined ? null : now,
      updatedAt: now,
    })
  }

  await batch.commit()
}

/**
 * Delete a receipt record that is too malformed to load.
 *
 * The normal delete needs a parsed `Receipt` to find its stored file, which is
 * exactly what an unreadable document cannot give. Everything for a receipt
 * lives under one folder, so the folder is listed and emptied instead — no
 * parsing required. Without this, a record that fails validation can be
 * reported but never cleared, and the warning about it is permanent.
 */
export async function purgeReceiptRecord(
  userId: string,
  receiptId: string,
): Promise<void> {
  const folder = ref(storage, `users/${userId}/receipts/${receiptId}`)
  try {
    const listing = await listAll(folder)
    await Promise.all(listing.items.map((item) => deleteObject(item)))
  } catch (cause) {
    // A missing folder is fine; the document is what the user sees. Anything
    // else still shouldn't block removing the broken record.
    const code = (cause as { code?: string }).code
    if (code !== 'storage/object-not-found') {
      console.warn('Could not clear stored files for receipt', receiptId, cause)
    }
  }
  // Clear the id from every charge that lists it, before the document goes.
  //
  // Without this the id survives its document, and a dangling id is not inert:
  // reading a missing receipt is *denied* by the rules rather than returning
  // nothing, so any later operation that reads a charge's receipts — merging
  // two charges, most visibly — fails outright with "Missing or insufficient
  // permissions" and cannot be made to work from the client. Six charges in
  // the live ledger already carried one when this was found, on 2026-11-03.
  const listing = await getDocs(
    query(
      collection(db, COLLECTIONS.transactions),
      where('userId', '==', userId),
      where('receiptIds', 'array-contains', receiptId),
    ),
  )
  await Promise.all(
    listing.docs.map((charge) =>
      updateDoc(charge.ref, { receiptIds: arrayRemove(receiptId), updatedAt: new Date() }),
    ),
  )

  // Marked deleted for the same reason the ordinary delete marks it: an
  // extraction still in flight finds the document absent and would otherwise
  // recreate it.
  await setDoc(doc(db, COLLECTIONS.deletedReceipts, receiptId), {
    userId,
    deletedAt: new Date().toISOString(),
  })
  await deleteDoc(doc(db, COLLECTIONS.receipts, receiptId))
}

/**
 * Delete a receipt and its stored original.
 *
 * Three writes, in a deliberate order: the charges that list it, the record
 * itself with a marker saying it was deleted, and then the file. The marker is
 * what stops an extraction already in flight from recreating the receipt —
 * see `COLLECTIONS.deletedReceipts`.
 */
export async function deleteReceipt(receipt: Receipt): Promise<void> {
  // Every charge still listing this receipt, in any year. `receiptIds` is what
  // the packet is assembled from and what the screens read, so leaving an id
  // there for a receipt that no longer exists is a lie in the one field that
  // is supposed to be authoritative. Nothing breaks today — a missing document
  // simply drops out of every view — but the field stops being trustworthy,
  // and the next feature to read it inherits the problem.
  //
  // `array-contains` beside an equality filter needs a composite index; it is
  // in `firestore.indexes.json`, added with this query (see CLAUDE.md).
  const charges = await getDocs(
    query(
      collection(db, COLLECTIONS.transactions),
      where('userId', '==', receipt.userId),
      where('receiptIds', 'array-contains', receipt.id!),
    ),
  )

  const batch = writeBatch(db)
  for (const charge of charges.docs) {
    batch.update(charge.ref, {
      receiptIds: arrayRemove(receipt.id!),
      updatedAt: new Date(),
    })
  }
  batch.delete(doc(db, COLLECTIONS.receipts, receipt.id!))
  // Written with the delete, not after it, so the two cannot disagree. A
  // reading still in flight would otherwise find the document absent and
  // recreate it — fully formed and pointing at a file that is about to go.
  batch.set(doc(db, COLLECTIONS.deletedReceipts, receipt.id!), {
    userId: receipt.userId,
    deletedAt: new Date().toISOString(),
  })
  await batch.commit()

  // The file goes last. If this throws, the record is already gone and the
  // orphaned object is invisible; the other order leaves a receipt on screen
  // whose image has been deleted.
  try {
    await deleteObject(ref(storage, receipt.originalFileUrl))
  } catch (cause) {
    const code = (cause as { code?: string }).code
    if (code !== 'storage/object-not-found') throw cause
  }
}
